import { useState, useMemo } from "react";
import { ChevronRight } from "lucide-react";
import { Group as Thread, Contact } from "@/data/schemas";
import { useActions, useAppState } from "@/state/legacy_module_api";
import { contactPrimaryLabel, contactSecondaryLabel, contactStatusFlags, normalizeContacts } from "@/features/contacts/contacts_list_model";

export interface UsersListProps {
  users?: Contact[];
  threadsList?: Thread[];
  onUserClick?: (user: Contact) => void;
  onProfilePictureClick?: (user: Contact, event?: React.MouseEvent) => void;
}

export default function UsersList({
  users: propsUsers,
  threadsList,
  onUserClick,
  onProfilePictureClick,
}: UsersListProps) {
  const [loadingUserId, setLoadingUserId] = useState<string | null>(null);
  const threadActions = useActions().thread as any;
  const createThread = threadActions?.createThread || threadActions?.selectThread;
  // Default to usersList from store if not provided
  const { usersList: storeUsersList } = useAppState().user;
  const users = propsUsers ?? storeUsersList ?? [];

  const normalizedContacts = useMemo(() => normalizeContacts(users, undefined), [users]);

  const handleUserClick = async (user: Contact) => {
    if (onUserClick) {
      onUserClick(user);
      return;
    }

    if (!threadsList || !user.userId) return;

    setLoadingUserId(user.userId);
    try {
      const existingThread = threadsList.find((thread) =>
        thread.members?.some((m) => m.userId === user.userId)
      );

      if (existingThread) {
        await createThread({ thread: existingThread });
      } else {
        await createThread({ withUserId: user.userId });
      }
    } catch (error) {
      console.error("Failed to start thread", error);
    } finally {
      setLoadingUserId(null);
    }
  };

  const handleProfilePictureClick = (user: Contact, event: React.MouseEvent) => {
    event.stopPropagation();
    onProfilePictureClick?.(user, event);
  };

  return (
    <div className="space-y-2">
      {normalizedContacts.map((user: Contact) => {
        const statusFlags = contactStatusFlags(user as any);
        return (
          <div
            key={user.userId}
            className="flex items-center justify-between p-3 rounded-lg hover:bg-muted cursor-pointer"
            onClick={() => handleUserClick(user)}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-12 h-12 rounded-full bg-muted flex items-center justify-center"
                onClick={(e) => handleProfilePictureClick(user, e)}
              >
                {user.displayPhoto ? (
                  <img
                    src={user.displayPhoto}
                    alt={user.username || user.userId}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <span className="text-lg font-semibold">
                    {(user.username || user.userId || "?").charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div>
                <div className="font-medium">{contactPrimaryLabel(user as any)}</div>
                <div className="text-sm text-muted-foreground">
                  {contactSecondaryLabel(user as any)}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {statusFlags.isBlocked && (
                <span className="text-xs text-red-500">Blocked</span>
              )}
              {statusFlags.isMuted && (
                <span className="text-xs text-yellow-500">Muted</span>
              )}
              {loadingUserId === user.userId ? (
                <div className="w-4 h-4 border-2 border-t-transparent border-primary rounded-full animate-spin" />
              ) : (
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
